import { View, XStack, YStack } from 'tamagui';

import PSText from '@/components/ui/PSText';
import PSBox from './ui/PSBox';

/**
 * CheckoutCart — the itemised cart box on the payment confirmation screen
 * (molecule).
 *
 * Displays a list of booked ticket tiers with full descriptions, quantities,
 * and line subtotals, then a total row. Layout: description on left (wraps),
 * quantity (X) and price right-aligned in separate columns. Header above
 * labels the list "Ticket(s)".
 *
 * Render-only, and the money arrives FORMATTED. Every amount in this box is a
 * sum (unit price × quantity, and their total), and the app works those out
 * where the cart lives — one place that knows the currency and the "0 reads
 * Free" rule (see `src/lib/eventCart`). This box prints the strings it's given.
 */
export type CheckoutCartLine = {
  /** Stable key, e.g. "tier-1". */
  id: string;
  /** Full tier description shown on the left, e.g. "General Admission — StandingRoom, Limited Access". */
  name: string;
  /** Tickets booked for this tier. */
  quantity: number;
  /** LINE subtotal (unit price × quantity), formatted — "Free" at zero. */
  subtotalDisplay: string;
};

export default function CheckoutCart({
  lines,
  totalDisplay,
  headerLabel = 'Ticket(s)',
  subtotalDisplay,
  couponDiscountDisplay,
}: {
  lines: CheckoutCartLine[];
  /** Sum of every line's subtotal, formatted — "Free" at zero. */
  totalDisplay: string;
  /** Header text for the section — defaults to "Ticket(s)". */
  headerLabel?: string;
  // Both set together once a coupon is applied; `totalDisplay` is then post-discount.
  subtotalDisplay?: string;
  couponDiscountDisplay?: string;
}) {
  return (
    <YStack gap="$md">
      <PSText.H1 color="$textDark">{headerLabel}</PSText.H1>

      <PSBox gap={'$space.lg'}>
        <YStack gap="$space.md">
          {lines.map((line) => (
            <XStack key={line.id} items="flex-start">
              <View flexBasis="60%" flexGrow={1} pr={'$space.sm'}>
                <PSText.Body2 color="$textDark" numberOfLines={3}>
                  {line.name}
                </PSText.Body2>
              </View>

              <View flexBasis="15%" flexShrink={0}>
                <PSText.Body2 color="$textDark">× {line.quantity}</PSText.Body2>
              </View>
              <XStack flexBasis="25%" flexGrow={1} justifyContent="flex-end">
                <PSText.Body2
                  color="$textDark"
                  fontWeight="$medium"
                  numberOfLines={3}
                >
                  {line.subtotalDisplay}
                </PSText.Body2>
              </XStack>
            </XStack>
          ))}
        </YStack>

        {couponDiscountDisplay && (
          <>
            <YStack
              borderTopWidth="$borderWidth.hairline"
              borderColor="$borderColor"
            />
            <YStack gap="$space.sm">
              {subtotalDisplay && (
                <XStack justify="space-between" items="center" gap="$lg">
                  <PSText.Body2 color="$textDark">Subtotal</PSText.Body2>
                  <PSText.Body2 color="$textDark">
                    {subtotalDisplay}
                  </PSText.Body2>
                </XStack>
              )}
              <XStack justify="space-between" items="center" gap="$lg">
                <PSText.Body2>Coupon discount</PSText.Body2>
                <PSText.Body2 color="$success">
                  {couponDiscountDisplay}
                </PSText.Body2>
              </XStack>
            </YStack>
          </>
        )}

        <YStack
          borderTopWidth="$borderWidth.hairline"
          borderColor="$borderColor"
        />

        <XStack justify="space-between" items="center" gap="$lg">
          <PSText.H2 color="$textDark">Total</PSText.H2>
          <PSText.H2 color="$textDark">{totalDisplay}</PSText.H2>
        </XStack>
      </PSBox>
    </YStack>
  );
}
